import isIP from "validator/es/lib/isIP";
import * as notifications from "../../notifications/notifications";
import {
  NETWORK_TAB_KEYS,
  PREVIEW_DATA_KEYS,
  URL_PREVIEW_DATA_ITEMS,
  VALID_URL_PROTOCOL_REGEX
} from "./consts";
import { refangUrlInText } from "../search/components/utils";
import type {
  DomainReport,
  IPReport,
  ThreatIntelProps,
  UrlReport
} from "./@types";
import { DownloadedFile as DomainDownloadedFile } from "./@types/Domain";
import { DownloadedFile as IPDownloadedFile } from "./@types/IP";
import { Analysis, NetworkRepEntry } from "./@types/Url";
import {
  domainReportCategoriesIssue,
  domainReportDownloadedFilesIssue,
  domainReportLastSeenIssue,
  domainReportThirdPartyRepIssue,
  domainReportTopThreatIssue,
  ipReportCategoriesIssue,
  ipReportDownloadedFilesIssue,
  ipReportThirdPartyRepIssue,
  ipReportTopThreatIssue,
  urlReportCategoriesIssue,
  urlReportClassificationIssue,
  urlReportLastAnalysisIssue,
  urlReportThirdPartyRepIssue,
  urlReportTopThreatIssue
} from "./issuepredicates";

export const getDomainFromUrl = (url: string) => {
  let urlObj;
  try {
    urlObj = new URL(url);
  } catch (e) {
    if (!url.includes("http") && !url.includes("https")) {
      return undefined;
    }
    return undefined;
  }
  return urlObj.hostname;
};

export const getVisibleTabs = ({ url, domain, ip }: ThreatIntelProps) => ({
  [NETWORK_TAB_KEYS.URL_ANALYSIS]: !!url,
  [NETWORK_TAB_KEYS.DOMAIN_ANALYSIS]: !!domain,
  [NETWORK_TAB_KEYS.IP_ANALYSIS]: !!ip
});

export const removeSurroundingQuotes = (str: string) =>
  str.replace(/^"(.*)"$/, "$1");

const extractIPFromDomainReport = (domainReport?: DomainReport) =>
  domainReport?.last_dns_records.find(item => item.type === "A")?.value;

export const extractIPfromNetworkThreatReports = ({
  ipReport,
  urlReport,
  domainReport
}: {
  ipReport?: IPReport | undefined;
  urlReport?: UrlReport | undefined;
  domainReport?: DomainReport | undefined;
}) =>
  ipReport?.requested_ip ||
  urlReport?.analysis?.last_analysis?.serving_ip_address ||
  extractIPFromDomainReport(domainReport) ||
  (isIP(urlReport?.analysis?.last_analysis?.domain || "") &&
    urlReport?.analysis?.last_analysis?.domain) || // ex last_analysis?.domain: http://12.34.4.56 -> domain == ip
  "";

export interface Predicate<T> {
  (ntiReport: T | undefined): number;
}
export const countReportIssues = <T>(
  data: T,
  predicates: Predicate<T>[]
): number =>
  predicates?.reduce((sum, predicate) => sum + predicate(data), 0) ?? 0;

export const getUrlReportIssues = (urlReport: UrlReport | undefined) =>
  countReportIssues(urlReport, [
    urlReportCategoriesIssue,
    urlReportLastAnalysisIssue,
    urlReportTopThreatIssue,
    urlReportThirdPartyRepIssue,
    urlReportClassificationIssue
  ]);

export const getDomainReportIssues = (domainReport: DomainReport | undefined) =>
  countReportIssues(domainReport, [
    domainReportLastSeenIssue,
    domainReportCategoriesIssue,
    domainReportDownloadedFilesIssue,
    domainReportTopThreatIssue,
    domainReportThirdPartyRepIssue
  ]);

export const getIPReportIssues = (ipReport: IPReport | undefined) =>
  countReportIssues(ipReport, [
    ipReportCategoriesIssue,
    ipReportDownloadedFilesIssue,
    ipReportTopThreatIssue,
    ipReportThirdPartyRepIssue
  ]);

const getClassificationFromEntries = (
  entries: NetworkRepEntry[],
  url: string
) => {
  const entry = entries.find(e => e.requested_network_location === url);
  return entry?.classification || "unknown";
};

export const enrichData = (
  files: Array<DomainDownloadedFile | IPDownloadedFile>,
  entries: NetworkRepEntry[]
) =>
  files.map(file => ({
    ...file,
    url_classification: getClassificationFromEntries(
      entries,
      file.last_download_url
    )
  }));

export const analysesWithPreviewData = (analysisHistory?: Analysis[]) => {
  const itemsWithPreviewData =
    analysisHistory?.filter(historyItem =>
      URL_PREVIEW_DATA_ITEMS.some(value => value in historyItem)
    ) ?? [];

  return {
    analysesWithPreview: itemsWithPreviewData,
    hasPreviewData: itemsWithPreviewData.length > 0
  };
};

export const filterLatestScreenshotFromReport = (
  analysisHistory?: Analysis[]
) => {
  const analysesWithScreenshots =
    analysisHistory?.filter(
      historyItem => PREVIEW_DATA_KEYS.SCREENSHOT in historyItem
    ) ?? [];

  const latestScreenshot = analysesWithScreenshots.at(-1)?.screenshot_url;
  return {
    latestScreenshot
  };
};

/**
 * Prepend http:// to the url if it's missing and refang the URL
 */
export const prependHttpToUrl = (url?: string) => {
  if (!url) return "";
  const refangedUrl = refangUrlInText(url);
  const isValidProtocol = refangedUrl.match(VALID_URL_PROTOCOL_REGEX);
  return isValidProtocol ? refangedUrl : `http://${refangedUrl}`;
};
